import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Typography,
  Button,
  Tabs,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tab,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useSelector, useDispatch } from "react-redux";
import {
  deleteCategory,
  fetchCategories,
  listCategories,
  saveCategory,
  editCategory,
} from "../../../redux/slices/orgAdmin/categoriesOrgSlice";
import {
  StyledButton,
  StyledContainer,
  StyledTableCell,
} from "../../CSS/Styles";

export default function Categories() {
  const dispatch = useDispatch();
  const { categories, loading } = useSelector((state) => state.categories);
  const { companyId } = useSelector((state) => state.app);
  const { languagesList } = useSelector((state) => state.languages); // Dynamic languages
  const [deleteTitle, setDeleteTitle] = useState("");

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [promotionIdToDelete, setPromotionIdToDelete] = useState(null);
  const [rowToEdit, setRowToEdit] = useState(null);

  const sectionId = 3;

  useEffect(() => {
    dispatch(fetchCategories({ companyId, sectionId }));
  }, [dispatch, companyId]);

  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState(
    languagesList?.[0]?.code || "en"
  ); // Default to first language
  const [newCategory, setNewCategory] = useState({});

  // Initialize category state dynamically based on available languages
  useEffect(() => {
    const initialCategoryState = languagesList.reduce((acc, lang) => {
      acc[lang.code] = { category_name: "", category_description: "" };
      return acc;
    }, {});
    setNewCategory(initialCategoryState);
  }, [languagesList]);

  const handleTabChange = (event, newLang) => {
    setCurrentLanguage(newLang);
  };

  const handleInputChange = (lang, field, value) => {
    setNewCategory((prev) => ({
      ...prev,
      [lang]: { ...prev[lang], [field]: value },
    }));
  };

  const handleEditCategory = async (categoryId) => {
    setRowToEdit(categoryId.id);
    try {
      const categoryData = await dispatch(
        editCategory({ id: categoryId.id }) // Ensure correct ID
      ).then((res) => res.payload);

      if (!categoryData || !categoryData.languages) {
        console.error("No languages found in API response.");
        return;
      }

      if (!languagesList || languagesList.length === 0) {
        console.error("languagesList is empty or undefined.");
        return;
      }

      const prefilledCategory = languagesList.reduce((acc, lang) => {
        acc[lang.code] = {
          category_name:
            categoryData.languages?.[lang.code]?.category_name || "",
          category_description:
            categoryData.languages?.[lang.code]?.category_description || "",
        };
        return acc;
      }, {});

      setNewCategory(prefilledCategory);

      setCurrentLanguage(currentLanguage);

      setIsAddingCategory(true);
    } catch (error) {
      console.error("Error fetching category data:", error);
    }
  };

  const handleAddCategory = () => {
    setRowToEdit(null);
    const emptyCategory = languagesList.reduce((acc, lang) => {
      acc[lang.code] = { category_name: "", category_description: "" };
      return acc;
    }, {});
    setNewCategory(emptyCategory);
    setIsAddingCategory(true);
  };

  const handleSaveCategory = () => {
    const payload = rowToEdit ? { id: rowToEdit } : {};

    Object.entries(newCategory).forEach(([lang, data]) => {
      if (["en", "fr", "ar"].includes(lang)) {
        payload[lang] = {
          category_name: data.category_name?.trim() || "",
          category_description: data.category_description?.trim() || "",
        };
      }
    });

    dispatch(saveCategory({ categoryData: payload, sectionId })).then(() => {
      dispatch(fetchCategories({ companyId, sectionId }));
      setCurrentLanguage(languagesList?.[0]?.code || "en");
    });

    setIsAddingCategory(false);
  };

  const handleDeleteClick = (id, title) => {
    setPromotionIdToDelete(id);
    setDeleteDialogOpen(true);
    setDeleteTitle(title);
  };

  const confirmDelete = () => {
    dispatch(deleteCategory({ id: promotionIdToDelete, sectionId }))
      .then(() => {
        setDeleteDialogOpen(false);
        setPromotionIdToDelete(null);

        // Fetch updated promotions list after successful deletion
        dispatch(fetchCategories({ companyId, sectionId }));
      })
      .catch((error) => {
        console.error("Error deleting promotion:", error);
      });
  };

  return (
    <StyledContainer>
      <Paper elevation={3} sx={{ padding: 3 }}>
        {!isAddingCategory ? (
          <>
            <Typography variant="h5" sx={{ mb: 2 }}>
              Categories
            </Typography>
            <StyledButton
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddCategory}
              sx={{ marginBottom: 2 }}
              disabled={loading}
            >
              Add
            </StyledButton>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <StyledTableCell>Category Name</StyledTableCell>
                    <StyledTableCell>Category Description</StyledTableCell>
                    <StyledTableCell>Actions</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {
                    categories
                      ?.map((item) => {
                        return (
                          <TableRow key={item.id}>
                            <TableCell>{item.category_name || "-"}</TableCell>
                            <TableCell>
                              {item.category_description || "-"}
                            </TableCell>
                            <TableCell>
                              <IconButton
                                onClick={() => handleEditCategory(item)}
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                onClick={() =>
                                  handleDeleteClick(item.id, item.category_name)
                                }
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })
                      .filter(Boolean) // Remove null rows
                  }
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : (
          <Box>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <IconButton onClick={() => setIsAddingCategory(false)}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6">Category</Typography>
            </Box>
            {languagesList?.length > 1 && (
              <Tabs value={currentLanguage} onChange={handleTabChange}>
                {languagesList?.map((lang) => (
                  <Tab key={lang.code} label={lang.name} value={lang.code} />
                ))}
              </Tabs>
            )}

            <TextField
              sx={{ mt: 2 }}
              label="Category Name"
              value={newCategory[currentLanguage]?.category_name || ""}
              onChange={(e) =>
                handleInputChange(
                  currentLanguage,
                  "category_name",
                  e.target.value
                )
              }
              inputProps={{ maxLength: 255 }}
              helperText={`${(newCategory[currentLanguage]?.category_name || "").length}/255`}
              fullWidth
            />

            <TextField
              sx={{ mt: 2 }}
              label="Category Description"
              value={newCategory[currentLanguage]?.category_description || ""}
              onChange={(e) =>
                handleInputChange(
                  currentLanguage,
                  "category_description",
                  e.target.value
                )
              }
              inputProps={{ maxLength: 255 }}
              helperText={`${(newCategory[currentLanguage]?.category_description || "").length}/255`}
              fullWidth
            />

            <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
              <Button
                onClick={() => setIsAddingCategory(false)}
                sx={{ marginRight: 2 }}
                color="error"
                variant="contained"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveCategory}
                color="primary"
                variant="contained"
              >
                Save
              </Button>
            </Box>
          </Box>
        )}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete{" "}
              <span style={{ fontWeight: "bold" }}> {deleteTitle}</span>? This
              action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button color="error" onClick={confirmDelete}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </StyledContainer>
  );
}
